import React, { useEffect, useMemo } from 'react';
import { DatePicker } from '~/components/Form/DatePicker';
import { Select } from '~/components/Select';
import {
    ProviderTile,
    ReferralAction,
    ReferralAttachmentType,
    ReferralAttachments,
    ReferralDisabledAlert,
} from '~/components/Patient/Referrals';
import { ServicesContext } from '~/services';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { ProviderType } from '~/util/providerUtils';
import { useReferralService } from '~/services/ReferralService';
import { OrgFormFieldType } from '~/services/ReferralService/useGetReferralFieldSettings';
import {
    ReferringProviderOptionType,
    formatReferringProviderLabel,
    formatReferringProviderName,
} from '~/services/ReferralService/useGetReferringProviders';
import { calculateMinDateFromConstraint } from '~/util/referralUtils';
import { matchReferringProvider } from '~/util/matchReferringProvider';
import { DiagnosisCodePicker } from '../DiagnosisCodePicker';

export interface IcdCodeOptionType {
    value: number;
    label: string;
    code: string;
    description?: string;
    primary: boolean;
}

export interface CareRequestTypeOptionType {
    value: string;
    label: string;
}

export interface ReferralOrgFieldsState {
    startDate: OrgFormFieldType | null;
    endDate: OrgFormFieldType | null;
    icdCode: OrgFormFieldType | null;
    visitNumber: OrgFormFieldType | null;
    careRequestType: OrgFormFieldType | null;
}

export interface ReferralInfoProps {
    planId: number | undefined;
    patient: PatientType;
    planCode: string | undefined;
}

export function ReferralInfo({ planId, patient, planCode }: ReferralInfoProps) {
    const context = React.useContext(ServicesContext);
    const { selectedProvider, clearSelectedProvider } = React.useContext(
        ReferralContext
    ) as ReferralContextType;
    // this component only renders if selectedProvider is not null
    const provider = selectedProvider as ProviderType;
    const { getReferralFieldSettings, getReferringProviders } = useReferralService();
    const hasCoreAppAccount = !!context.user.getExternalAccount('Core App');

    const [reason, setReason] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [sendingReferral, setSendingReferral] = React.useState(false);
    const [attachments, setAttachments] = React.useState<ReferralAttachmentType[]>([]);
    const [suggestedEdit, setSuggestedEdit] = React.useState(false);
    const [missingRequiredLocation, setMissingRequiredLocation] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState<undefined | LocationType>(() => {
        if (provider.locations?.length === 1) {
            return provider.locations[0];
        }
        return provider.locations?.find((x) => x.is_default);
    });
    const [startDate, setStartDate] = React.useState<Date | null>(null);
    const [endDate, setEndDate] = React.useState<Date | null>(null);
    const [visitNumber, setVisitNumber] = React.useState<number | null>(null);
    const [careRequestType, setCareRequestType] = React.useState<CareRequestTypeOptionType | null>(
        null
    );
    const [referringProviderOptions, setReferringProviderOptions] = React.useState<
        ReferringProviderOptionType[]
    >([]);
    const [referringProvidersLoaded, setReferringProvidersLoaded] = React.useState(false);
    const [referringProvider, setReferringProvider] =
        React.useState<ReferringProviderOptionType | null>(null);
    const [selectedDiagnosisCodes, setSelectedDiagnosisCodes] = React.useState<IcdCodeOptionType[]>(
        []
    );
    const [priority, setPriority] = React.useState(1);
    const [orgFormFields, setOrgFormFields] = React.useState<ReferralOrgFieldsState>({
        startDate: null,
        endDate: null,
        icdCode: null,
        visitNumber: null,
        careRequestType: null,
    });

    const currentDate = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    useEffect(() => {
        if (selectedLocation && missingRequiredLocation) {
            setMissingRequiredLocation(false);
        }
    }, [selectedLocation, missingRequiredLocation]);

    // load the WellMed Hub's configured fields from core app
    useEffect(() => {
        async function loadFields() {
            const fields = await getReferralFieldSettings();
            const startDateField = fields.find(
                (field) => field.form_field.slug === 'send_referral_start_date'
            );
            const endDateField = fields.find(
                (field) => field.form_field.slug === 'send_referral_end_date'
            );
            const icdCodeField = fields.find(
                (field: OrgFormFieldType) =>
                    field.form_field.slug === 'send_referral_diagnosis_code'
            );
            const visitNumberField = fields.find(
                (field: OrgFormFieldType) => field.form_field.slug === 'send_referral_visit_number'
            );
            const careRequestTypeField = fields.find(
                (field: OrgFormFieldType) =>
                    field.form_field.slug === 'send_referral_care_request_type'
            );

            // default start date to current date
            if (startDateField) {
                const defaultStartDate = new Date();
                defaultStartDate.setHours(0, 0, 0, 0);
                setStartDate(defaultStartDate);
            }

            if (endDateField?.default_value) {
                const [year, month, day] = String(endDateField.default_value)
                    .split('-')
                    .map(Number);
                setEndDate(new Date(year, month - 1, day));
            }

            if (visitNumberField?.default_value != null) {
                setVisitNumber(Number(visitNumberField?.default_value));
            }

            setOrgFormFields({
                startDate: startDateField || null,
                endDate: endDateField || null,
                icdCode: icdCodeField || null,
                visitNumber: visitNumberField || null,
                careRequestType: careRequestTypeField || null,
            });
        }
        loadFields();
    }, [getReferralFieldSettings]);

    // load the primary org's active providers (Core App-linked users only)
    useEffect(() => {
        if (!hasCoreAppAccount) {
            return;
        }
        async function loadReferringProviders() {
            try {
                const providers = await getReferringProviders();
                const enabledProviders = providers.filter(
                    (referringProviderOption) => referringProviderOption.enabled
                );
                const options = enabledProviders.map((referringProviderOption) => ({
                    value: referringProviderOption.id,
                    label: formatReferringProviderLabel(referringProviderOption),
                    name: formatReferringProviderName(referringProviderOption),
                }));
                setReferringProviderOptions(options);
                setReferringProvidersLoaded(true);

                // Auto-fill the referring provider from the patient's PCP name when it
                // resolves to exactly one active provider.
                const matchedProvider = matchReferringProvider(
                    patient.pcps[0]?.name,
                    enabledProviders
                );
                if (matchedProvider) {
                    const matchedOption = options.find(
                        (option) => option.value === matchedProvider.id
                    );
                    if (matchedOption) {
                        setReferringProvider(matchedOption);
                    }
                }
            } catch (error) {
                setReferringProviderOptions([]);
                setReferringProvidersLoaded(true);
            }
        }
        loadReferringProviders();
    }, [getReferringProviders, hasCoreAppAccount, patient.pcps]);

    const isSubmitDisabled = useMemo(() => {
        const hasStartDateError = orgFormFields.startDate?.required && !startDate;
        const hasEndDateError = orgFormFields.endDate?.required && !endDate;
        const hasDiagnosisCodeError =
            orgFormFields.icdCode?.required && selectedDiagnosisCodes.length === 0;
        const hasVisitNumberError = orgFormFields.visitNumber?.required && visitNumber === null;
        const hasCareRequestTypeError = orgFormFields.careRequestType?.required && !careRequestType;
        return !!(
            reason.trim().length === 0 ||
            hasStartDateError ||
            hasEndDateError ||
            hasDiagnosisCodeError ||
            hasVisitNumberError ||
            hasCareRequestTypeError
        );
    }, [
        startDate,
        endDate,
        reason,
        selectedDiagnosisCodes,
        visitNumber,
        careRequestType,
        orgFormFields,
    ]);

    return (
        <>
            <ReferralDisabledAlert provider={provider} setSuggestedEdit={setSuggestedEdit} />
            <div className="row mb-3">
                <div className="col">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={clearSelectedProvider}
                    >
                        Back
                    </button>
                </div>
                <div className="col text-end">
                    {context.user.getExternalAccount('Core App') && (
                        <ReferralAttachments
                            patient={patient}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            sendingReferral={sendingReferral}
                        />
                    )}
                    <ReferralAction
                        patient={patient}
                        provider={provider}
                        planId={planId}
                        reason={reason}
                        notes={notes}
                        email={email}
                        attachments={attachments}
                        sendingReferral={sendingReferral}
                        setSendingReferral={setSendingReferral}
                        selectedLocation={selectedLocation}
                        setMissingRequiredLocation={setMissingRequiredLocation}
                        suggestedEdit={suggestedEdit}
                        startDate={startDate}
                        endDate={endDate}
                        diagnosisCodes={selectedDiagnosisCodes}
                        visitNumber={visitNumber}
                        careRequestType={careRequestType}
                        isSubmitDisabled={isSubmitDisabled}
                        orgFormFields={orgFormFields}
                        priority={priority}
                        referringProviderId={referringProvider?.value ?? null}
                        fromProviderName={referringProvider?.name}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-6 col-lg-6">
                    <div className="mb-3">
                        <label htmlFor="referral-reason" className="fw-bold mb-1">
                            Reason for Referral
                        </label>
                        <input
                            id="referral-reason"
                            type="text"
                            className="form-control"
                            placeholder="Provide clinical context"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="referral-notes" className="fw-bold mb-1">
                            Notes to Provider
                        </label>
                        <textarea
                            id="referral-notes"
                            className="form-control"
                            rows={4}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                    {hasCoreAppAccount && (
                        <div className="mb-3">
                            <label htmlFor="referring-provider" className="fw-bold mb-1">
                                Referring Provider
                            </label>
                            {referringProviderOptions.length > 0 ? (
                                <Select
                                    menuPosition="fixed"
                                    id="referring-provider"
                                    label="referring-provider"
                                    isClearable
                                    options={referringProviderOptions}
                                    value={referringProvider}
                                    onChange={setReferringProvider}
                                    placeholder="Select a referring provider"
                                />
                            ) : (
                                referringProvidersLoaded && (
                                    <div className="text-muted">
                                        No providers found. Add one in your LeadingReach
                                        organization.
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    <div className="mb-3">
                        <label htmlFor="referral-email" className="fw-bold mb-1">
                            Patient Email
                            <i
                                className="fa-regular fa-question-circle ms-2"
                                title='A referral confirmation will be emailed to the address you enter after you click "Send".'
                            />
                        </label>
                        <input
                            id="referral-email"
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    {orgFormFields.startDate && (
                        <div className="mb-3">
                            <label htmlFor="start-date" className="fw-bold mb-1">
                                Start Date
                            </label>
                            <div>
                                <DatePicker
                                    name="start-date"
                                    id="start-date"
                                    value={startDate}
                                    onChange={(val: null | Date) => setStartDate(val)}
                                    minDate={calculateMinDateFromConstraint(
                                        orgFormFields.startDate.metadata?.constraints
                                            ?.greater_than_or_equal_to,
                                        currentDate
                                    )}
                                    maxDate={currentDate}
                                />
                            </div>
                        </div>
                    )}
                    {orgFormFields.endDate && (
                        <div className="mb-3">
                            <label htmlFor="end-date" className="fw-bold mb-1">
                                End Date
                            </label>
                            <div>
                                <DatePicker
                                    name="end-date"
                                    id="end-date"
                                    value={endDate}
                                    onChange={(val: null | Date) => setEndDate(val)}
                                    minDate={new Date()}
                                />
                            </div>
                        </div>
                    )}
                    {orgFormFields.icdCode && (
                        <div className="mb-3">
                            <label htmlFor="dx-code" className="fw-bold mb-1">
                                Diagnosis Code
                            </label>
                            <div>
                                <DiagnosisCodePicker
                                    inputId="dx-code"
                                    value={selectedDiagnosisCodes}
                                    onChange={setSelectedDiagnosisCodes}
                                />
                                <div className="text-muted">
                                    Click the star to mark a diagnosis code as primary.
                                </div>
                            </div>
                        </div>
                    )}
                    {orgFormFields.visitNumber && (
                        <div className="mb-3">
                            <label htmlFor="visit-number" className="fw-bold mb-1">
                                Number of Visits
                            </label>
                            <input
                                id="visit-number"
                                type="text"
                                className="form-control w-25"
                                value={visitNumber !== null ? visitNumber : ''}
                                onChange={(e) => {
                                    setVisitNumber(
                                        e.target.value && Number(e.target.value) >= 0
                                            ? Number(e.target.value)
                                            : null
                                    );
                                }}
                            />
                        </div>
                    )}
                    {orgFormFields.careRequestType && (
                        <div className="mb-3">
                            <label htmlFor="care-request-type" className="fw-bold mb-1">
                                Care Request Type
                            </label>
                            <Select
                                menuPosition="fixed"
                                id="care-request-type"
                                label="care-request-type"
                                isClearable
                                options={(
                                    orgFormFields.careRequestType.metadata?.dropdown_options ?? []
                                ).map((opt) => ({ value: opt, label: opt }))}
                                value={careRequestType}
                                onChange={setCareRequestType}
                                placeholder="Select a care request type"
                            />
                        </div>
                    )}
                    <div className="mb-3">
                        <div className="fw-bold mb-1">Priority</div>
                        <div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="referral-priority"
                                    id="priority-normal"
                                    value="1"
                                    checked={priority === 1}
                                    onChange={(e) => setPriority(Number(e.target.value))}
                                />
                                <label className="form-check-label" htmlFor="priority-normal">
                                    Normal
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="referral-priority"
                                    id="priority-time-sensitive"
                                    value="2"
                                    checked={priority === 2}
                                    onChange={(e) => setPriority(Number(e.target.value))}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="priority-time-sensitive"
                                >
                                    Time-sensitive
                                </label>
                            </div>
                            <div className="form-check form-check-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="referral-priority"
                                    id="priority-urgent"
                                    value="3"
                                    checked={priority === 3}
                                    onChange={(e) => setPriority(Number(e.target.value))}
                                />
                                <label className="form-check-label" htmlFor="priority-urgent">
                                    Urgent
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-2 d-none d-lg-block" />
                <ProviderTile
                    provider={provider}
                    showLocations
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                    missingRequiredLocation={missingRequiredLocation}
                    setSuggestedEdit={setSuggestedEdit}
                    planCode={planCode}
                />
            </div>
        </>
    );
}
